// Redux selectors for KPI Dashboard

import { createSelector } from 'reselect';

const selectKpiDashboardState = (state) => state.kpiDashboard || {};

export const selectMyPeriods = createSelector(
    [selectKpiDashboardState],
    (dashboardState) => dashboardState.myPeriods || []
);

export const selectSelectedPeriodId = createSelector(
    [selectKpiDashboardState],
    (dashboardState) => dashboardState.selectedPeriodId
);

export const selectSelectedPeriod = createSelector(
    [selectMyPeriods, selectSelectedPeriodId],
    (periods, selectedPeriodId) => periods.find(period => period.id === selectedPeriodId) || null
);

export const selectMyKpi = createSelector(
    [selectKpiDashboardState],
    (dashboardState) => dashboardState.myKpi
);

export const selectLoading = createSelector(
    [selectKpiDashboardState],
    (dashboardState) => dashboardState.loading || false
);

export const selectPeriodsLoading = createSelector(
    [selectKpiDashboardState],
    (dashboardState) => dashboardState.periodsLoading || false
);

export const selectKpiLoading = createSelector(
    [selectKpiDashboardState],
    (dashboardState) => dashboardState.kpiLoading || false
);

export const selectError = createSelector(
    [selectKpiDashboardState],
    (dashboardState) => dashboardState.error
);

export const selectPeriodsError = createSelector(
    [selectKpiDashboardState],
    (dashboardState) => dashboardState.periodsError
);

export const selectKpiError = createSelector(
    [selectKpiDashboardState],
    (dashboardState) => dashboardState.kpiError
);

// Selectors for KPI Dashboard

// Select user KPI periods
export const selectUserKpiPeriods = (state) => {
    const periods = state.kpiDashboard?.userKpiPeriods;
    return Array.isArray(periods) ? periods : [];
};

// Select user KPI details
export const selectUserKpiDetails = (state) => state.kpiDashboard.userKpiDetails;

// Select loading state
export const selectDashboardLoading = (state) => state.kpiDashboard.loading;

// Select error state
export const selectDashboardError = (state) => state.kpiDashboard.error;

// Select current period (if available)
export const selectCurrentPeriod = (state) => {
    const periods = selectUserKpiPeriods(state);
    return periods.find(period => period.isCurrent) || periods[0] || null;
};

// Select overall score from KPI details

// Select employee preview data
export const selectEmployeePreview = (state) => state.kpiDashboard.employeePreview || [];
export const selectEmployeePreviewLoading = (state) => state.kpiDashboard.employeePreviewLoading;
export const selectEmployeePreviewError = (state) => state.kpiDashboard.employeePreviewError;
export const selectOverallScore = (state) => {
    const details = selectUserKpiDetails(state);
    return details?.overallScore || 0;
};

// Select goals from KPI details
export const selectGoals = (state) => {
    const details = selectUserKpiDetails(state);
    return details?.goals || [];
};

// Select user information from KPI details
export const selectUserInfo = (state) => {
    const details = selectUserKpiDetails(state);
    return details?.user || {};
};

// Select organizational info
export const selectOrganizationalInfo = (state) => {
    const details = selectUserKpiDetails(state);
    return {
        designation: details?.designation,
        department: details?.department,
        manager: details?.manager,
        directReports: details?.directReports || [],
        roles: details?.roles || []
    };
};

// Computed selectors for KPI calculations
export const selectKpiCalculations = createSelector(
    [selectMyKpi],
    (kpi) => {
        if (!kpi || !kpi.goals || kpi.goals.length === 0) {
            return {
                totalGoals: 0,
                goalsWithScores: 0,
                overallScore: 0,
                overallPercentage: 0,
                goalBreakdown: [],
                performanceLevel: 'No Data'
            };
        }

        const goals = kpi.goals;
        const totalGoals = goals.length;
        let totalWeightedScore = 0;
        let totalWeight = 0;
        let goalsWithScores = 0;
        const goalBreakdown = [];

        goals.forEach(goal => {
            const weight = Number(goal.weight) || 0;

            if (goal.scores && goal.scores.length > 0) {
                goalsWithScores++;
                totalWeight += weight;

                // Calculate average score for this goal
                const totalScore = goal.scores.reduce((sum, score) => sum + Number(score.score), 0);
                const averageScore = totalScore / goal.scores.length;

                // Calculate weighted score
                const weightedScore = (averageScore / 10) * weight;
                totalWeightedScore += weightedScore;

                goalBreakdown.push({
                    id: goal.id,
                    name: goal.goal?.name || 'Unknown Goal',
                    weight,
                    averageScore,
                    weightedScore,
                    scoreCount: goal.scores.length,
                    category: goal.category
                });
            } else {
                goalBreakdown.push({
                    id: goal.id,
                    name: goal.goal?.name || 'Unknown Goal',
                    weight,
                    averageScore: 0,
                    weightedScore: 0,
                    scoreCount: 0,
                    category: goal.category
                });
            }
        });

        const overallPercentage = totalWeight > 0 ? (totalWeightedScore / totalWeight) * 100 : 0;
        const rawScore = overallPercentage / 10;
        const fractional = rawScore - Math.floor(rawScore);
        const overallScore = fractional >= 0.9 ? Math.ceil(rawScore) : rawScore;

        // Determine performance level
        let performanceLevel = 'No Data';
        if (overallScore >= 9) performanceLevel = 'Excellent';
        else if (overallScore >= 7) performanceLevel = 'Good';
        else if (overallScore >= 5) performanceLevel = 'Average';
        else if (overallScore >= 3) performanceLevel = 'Needs Improvement';
        else if (overallScore > 0) performanceLevel = 'Critical';

        return {
            totalGoals,
            goalsWithScores,
            overallScore,
            overallPercentage,
            goalBreakdown,
            performanceLevel,
            totalWeightedScore,
            totalWeight
        };
    }
);

export const selectKpiChartData = createSelector(
    [selectKpiCalculations],
    (calculations) => {
        const { goalBreakdown } = calculations;

        return {
            goalPerformance: goalBreakdown.map(goal => ({
                name: goal.name.length > 20 ? goal.name.substring(0, 20) + '...' : goal.name,
                score: goal.averageScore,
                weight: goal.weight,
                fullName: goal.name
            })),
            scoreDistribution: goalBreakdown.reduce((acc, goal) => {
                const range = goal.averageScore >= 9 ? '9-10' :
                             goal.averageScore >= 7 ? '7-8.9' :
                             goal.averageScore >= 5 ? '5-6.9' :
                             goal.averageScore >= 3 ? '3-4.9' : '0-2.9';
                acc[range] = (acc[range] || 0) + 1;
                return acc;
            }, {}),
            categoryBreakdown: goalBreakdown.reduce((acc, goal) => {
                const category = goal.category || 'Uncategorized';
                if (!acc[category]) {
                    acc[category] = { count: 0, totalWeight: 0 };
                }
                acc[category].count += 1;
                acc[category].totalWeight += goal.weight;
                return acc;
            }, {})
        };
    }
);